import { Select, MultiSelect } from '@mantine/core'
import { ReactQuery } from 'cms-api'
import { Field, Form, Formik } from 'formik'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import {
  ROUTE,
  TRADE_APPLICATION_SURVEY,
  HOW_DID_YOU_HEAR_ABOUT_US_OPTIONS,
} from 'shared-constants'
import * as Yup from 'yup'
import { getPublicLayout } from '@public/components/PublicLayout'
import { Button } from 'components/Button'
import { Box, Flex } from 'components/Layout'
import { Text } from 'components/Typography'
import { COLOR_INTENT, FONT_SIZE, SPACE } from 'Theme'

export const TradeConfirmation = () => {
  const router = useRouter()
  const [isSurveySubmitted, setIsSurveySubmitted] = useState(false)
  const tradeApplicationId = router.query?.tradeApplicationId as string
  const postTradeApplicationSurvey = ReactQuery.usePostTradeApplicationSurveys()

  return (
    <Flex
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      my={SPACE.PX_40}
    >
      {isSurveySubmitted ? (
        <Flex
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          mt={SPACE.PX_40}
        >
          <Text
            fontSize={{ MOBILE: FONT_SIZE.PX_24, DESKTOP: FONT_SIZE.PX_32 }}
            fontWeight={600}
          >
            Thanks for submitting our Trade Application survey!
          </Text>
          <Flex justifyContent="center" mt={SPACE.PX_16} mb={SPACE.PX_32}>
            <Button
              width="initial"
              onClick={() => {
                router.push(ROUTE.SHOWROOM())
              }}
            >
              Shop Now
            </Button>
          </Flex>
        </Flex>
      ) : (
        <>
          <Text
            fontSize={{ MOBILE: FONT_SIZE.PX_24, DESKTOP: FONT_SIZE.PX_32 }}
            fontWeight={600}
          >
            Thanks for submitting your Trade Application!
          </Text>
          <Text mt={SPACE.PX_8} fontSize={FONT_SIZE.PX_20}>
            Help us expedite your application.
          </Text>
          <Formik
            initialValues={{
              designSpeciality: '',
              projectsCount: '',
              whatBringsYouToTheSite: '',
              teamSize: '',
              howDidYouHearAboutUs: [],
            }}
            validationSchema={Yup.object().shape({
              atLeastOne: Yup.mixed().test(
                'at-least-one-field',
                'At least one answer must be filled out',
                function () {
                  const {
                    designSpeciality,
                    projectsCount,
                    whatBringsYouToTheSite,
                    teamSize,
                    howDidYouHearAboutUs,
                    // eslint-disable-next-line react/no-this-in-sfc
                  } = this.parent

                  return (
                    designSpeciality ||
                    projectsCount ||
                    whatBringsYouToTheSite ||
                    teamSize ||
                    howDidYouHearAboutUs
                  )
                }
              ),
            })}
            onSubmit={async (values) => {
              if (values) {
                const res = await postTradeApplicationSurvey.mutateAsync({
                  data: {
                    data: {
                      designSpeciality: values.designSpeciality,
                      projectsCount: values.projectsCount,
                      whatBringsYouToTheSite: values.whatBringsYouToTheSite,
                      teamSize: values.teamSize,
                      tradeApplication: tradeApplicationId,
                      howDidYouHearAboutUs: values.howDidYouHearAboutUs,
                    },
                  },
                })
                if (res?.data) {
                  setIsSurveySubmitted(true)
                }
              }
            }}
          >
            {({ handleSubmit, setFieldValue, isSubmitting, errors }) => (
              <Form onSubmit={handleSubmit}>
                <Flex flexDirection="column" mt={SPACE.PX_40}>
                  <Box mb={SPACE.PX_16}>
                    <Field name="designSpeciality">
                      {({ field, meta }) => (
                        <Select
                          name={field.name}
                          label={
                            TRADE_APPLICATION_SURVEY.designSpeciality.question
                          }
                          placeholder="Select option..."
                          data={TRADE_APPLICATION_SURVEY.designSpeciality.answers.map(
                            (answer) => ({ label: answer, value: answer })
                          )}
                          error={
                            meta.touched && meta.error ? meta.error : undefined
                          }
                          value={field.value}
                          onChange={(value) => {
                            setFieldValue(field.name, value)
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                  <Box mb={SPACE.PX_16}>
                    <Field name="projectsCount">
                      {({ field, meta }) => (
                        <Select
                          name={field.name}
                          label={
                            TRADE_APPLICATION_SURVEY.projectsCount.question
                          }
                          placeholder="Select option..."
                          data={TRADE_APPLICATION_SURVEY.projectsCount.answers.map(
                            (answer) => ({ label: answer, value: answer })
                          )}
                          error={
                            meta.touched && meta.error ? meta.error : undefined
                          }
                          value={field.value}
                          onChange={(value) => {
                            setFieldValue(field.name, value)
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                  <Box mb={SPACE.PX_16}>
                    <Field name="whatBringsYouToTheSite">
                      {({ field, meta }) => (
                        <Select
                          name={field.name}
                          label={
                            TRADE_APPLICATION_SURVEY.whatBringsYouToTheSite
                              .question
                          }
                          placeholder="Select option..."
                          data={TRADE_APPLICATION_SURVEY.whatBringsYouToTheSite.answers.map(
                            (answer) => ({ label: answer, value: answer })
                          )}
                          error={
                            meta.touched && meta.error ? meta.error : undefined
                          }
                          value={field.value}
                          onChange={(value) => {
                            setFieldValue(field.name, value)
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                  <Box mb={SPACE.PX_16}>
                    <Field name="teamSize">
                      {({ field, meta }) => (
                        <Select
                          name={field.name}
                          label={TRADE_APPLICATION_SURVEY.teamSize.question}
                          data={TRADE_APPLICATION_SURVEY.teamSize.answers.map(
                            (answer) => ({ label: answer, value: answer })
                          )}
                          placeholder="Select option..."
                          error={
                            meta.touched && meta.error ? meta.error : undefined
                          }
                          value={field.value}
                          onChange={(value) => {
                            setFieldValue(field.name, value)
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                  <Box mb={SPACE.PX_16}>
                    <Field name="howDidYouHearAboutUs">
                      {({ field, meta }) => (
                        <MultiSelect
                          name={field.name}
                          label={
                            TRADE_APPLICATION_SURVEY.howDidYouHearAboutUs
                              .question
                          }
                          placeholder="Select option..."
                          data={HOW_DID_YOU_HEAR_ABOUT_US_OPTIONS}
                          error={
                            meta.touched && meta.error ? meta.error : undefined
                          }
                          value={field.value}
                          onChange={(value) => {
                            setFieldValue(field.name, value)
                          }}
                        />
                      )}
                    </Field>
                  </Box>
                  {/* @ts-ignore */}
                  {errors.atLeastOne && (
                    <Text
                      fontSize={FONT_SIZE.PX_12}
                      color={COLOR_INTENT.ERROR_DARK}
                    >
                      {/* @ts-ignore */}
                      {errors.atLeastOne}
                    </Text>
                  )}
                  <Flex
                    mt={SPACE.PX_15}
                    display="flex"
                    justifyContent="flex-start"
                  >
                    <Button
                      // add loading state
                      disabled={isSubmitting}
                      isLoading={isSubmitting}
                      width="100%"
                      type="submit"
                      label="Submit Trade Survey"
                      margin={0}
                    >
                      Submit
                    </Button>
                  </Flex>
                </Flex>
              </Form>
            )}
          </Formik>
        </>
      )}
    </Flex>
  )
}

TradeConfirmation.seoProps = {
  description: 'Thanks for submitting your Trade Application!',
}

TradeConfirmation.getLayout = (page) =>
  getPublicLayout(page, {
    isFullWidth: true,
  })
